import * as React from 'react';
import { HCard, FieldLabel } from '../h';

/* WifiCredentialsForm — the network-picker + password form, extracted from
   HearthWifiView (PLAN wifi-change-webapp §4.3) so the no-network QR page and the
   WiFi-management change flow share ONE form. The two callers differ only in what
   submit does (render a QR vs encrypt-and-send) — that stays with the caller; this
   component is purely the controlled fields.

   Everything the original form learned the hard way is preserved here:
   - The picker's <option> values are INDICES into the ssids array (plus the
     OTHER_SENTINEL), never the SSID strings themselves — a real network literally
     named "other" can never collide with the sentinel.
   - Every free-text input carries the noMangle attributes disabling autocorrect,
     auto-capitalisation, smart quotes and spellcheck: a silently "corrected" SSID or
     password produces a failure nobody can diagnose from the TV (BUG-146 class).
   - The password has a show/hide toggle because back-and-correct is first-class
     (~40% of people mistype the password first time).
   - When the CALLER REPLACES THE LIST, the customer's CHOSEN NETWORK stays chosen,
     not their list position — the device's list membership and ordering move
     between scans. If the chosen name is still offered the index is re-pointed; if
     it vanished the form drops to manual entry pre-filled with that name (an
     unambiguous state — never a blank control or a silent substitution). Without
     this, a stale index survives a wholesale list replacement and the submit
     targets whatever network now occupies that slot. The reconciliation lives
     HERE, in the shared form, so every caller gets it (it originated in
     HearthWifiView's hashchange handler and moved in with the fields). */

export const OTHER_SENTINEL = 'other';

/* The attributes that keep phone keyboards from silently rewriting what the
   customer types. Applied to every free-text field. */
export const noMangle = {
  autoComplete: 'off',
  autoCapitalize: 'none',
  autoCorrect: 'off',
  spellCheck: false,
};

/* The SSID the current selection denotes — the picker choice, or the manual text. */
export function effectiveSsidFrom(ssids, selection, manualSsid) {
  return selection === OTHER_SENTINEL ? manualSsid : ssids[Number(selection)] ?? '';
}

export function WifiIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 13a10 10 0 0 1 14 0" />
      <path d="M8.5 16.5a5 5 0 0 1 7 0" />
      <path d="M2 9.5a15 15 0 0 1 20 0" />
      <line x1="12" y1="20" x2="12.01" y2="20" />
    </svg>
  );
}

function EyeIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/**
 * Controlled form fields. The caller owns all state.
 *
 * @param ssids        string[] — networks offered by the picker ([] = manual only)
 * @param selection    index-string ('0', '1', …) or OTHER_SENTINEL
 * @param manualSsid   the manual-entry value (shown when selection is the sentinel
 *                     or no ssids were offered)
 * @param password     the password value
 * @param currentSsid  optional — the network the Hearth is on right now; the picker
 *                     labels it so the same-network case is visible BEFORE submitting
 * @param disabled     disables every field (while a change is in flight)
 * @param idPrefix     unique per page so two forms never share input ids
 */
export function WifiCredentialsForm({
  ssids,
  selection,
  onSelectionChange,
  manualSsid,
  onManualSsidChange,
  password,
  onPasswordChange,
  currentSsid = null,
  disabled = false,
  idPrefix = 'hw',
}) {
  const [showPw, setShowPw] = React.useState(false);
  const showManual = ssids.length === 0 || selection === OTHER_SENTINEL;

  // Reconcile the index-based selection across LIST REPLACEMENTS (see the header
  // comment): the chosen NAME survives, never the position. `selectionRef` tracks
  // the live selection (assigned every render) so the effect — keyed on the list
  // identity only — compares the new list against the selection as it stood.
  const selectionRef = React.useRef(selection);
  selectionRef.current = selection;
  const lastSsidsRef = React.useRef(ssids);
  React.useEffect(() => {
    const prevList = lastSsidsRef.current;
    if (prevList === ssids) return;
    lastSsidsRef.current = ssids;
    const sel = selectionRef.current;
    if (sel === OTHER_SENTINEL) return; // manual entry survives by itself
    const prevSsid = prevList[Number(sel)];
    const idx = prevSsid ? ssids.indexOf(prevSsid) : -1;
    if (idx >= 0) {
      if (String(idx) !== sel) onSelectionChange(String(idx));
    } else {
      onSelectionChange(OTHER_SENTINEL);
      if (prevSsid) onManualSsidChange(prevSsid);
    }
  }, [ssids, onSelectionChange, onManualSsidChange]);

  return (
    <>
      <FieldLabel htmlFor={`${idPrefix}-ssid`} className="mb-1.5">
        Network name (SSID)
      </FieldLabel>
      {ssids.length > 0 ? (
        <HCard className="px-3 py-2.5 flex items-center gap-2 mb-3">
          <WifiIcon className="w-4 h-4 text-slate-500 flex-shrink-0" />
          <select
            id={`${idPrefix}-ssid`}
            className="flex-1 text-[14px] outline-none bg-transparent text-slate-900"
            value={selection}
            onChange={(e) => onSelectionChange(e.target.value)}
            disabled={disabled}
          >
            {ssids.map((s, i) => (
              <option key={`${i}-${s}`} value={String(i)}>
                {currentSsid != null && s === currentSsid ? `${s} (connected now)` : s}
              </option>
            ))}
            <option value={OTHER_SENTINEL}>Other (enter manually)</option>
          </select>
        </HCard>
      ) : null}
      {showManual && (
        <HCard className="px-3 py-2.5 flex items-center gap-2 mb-3">
          <WifiIcon className="w-4 h-4 text-slate-500 flex-shrink-0" />
          <input
            id={ssids.length === 0 ? `${idPrefix}-ssid` : `${idPrefix}-ssid-manual`}
            className="flex-1 text-[14px] outline-none bg-transparent text-slate-900 placeholder:text-slate-400"
            value={manualSsid}
            onChange={(e) => onManualSsidChange(e.target.value)}
            placeholder="Your WiFi network name"
            disabled={disabled}
            {...noMangle}
          />
        </HCard>
      )}
      {showManual && (
        <p className="text-[11px] text-slate-400 mb-4 leading-relaxed">
          Type it exactly as it appears on your router {'—'} case and
          spaces matter.
        </p>
      )}

      <FieldLabel htmlFor={`${idPrefix}-pw`} className="mb-1.5">
        Password
      </FieldLabel>
      <HCard className="px-3 py-2.5 flex items-center gap-2 mb-1.5">
        <input
          id={`${idPrefix}-pw`}
          type={showPw ? 'text' : 'password'}
          className="flex-1 text-[14px] outline-none bg-transparent text-slate-900 placeholder:text-slate-400"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          placeholder="WiFi password"
          disabled={disabled}
          {...noMangle}
        />
        <button
          type="button"
          className="text-slate-400 hover:text-slate-600"
          onClick={() => setShowPw((v) => !v)}
          aria-label={showPw ? 'Hide password' : 'Show password'}
        >
          <EyeIcon className="w-4 h-4" />
        </button>
      </HCard>
      <p className="text-[11px] text-slate-400 mb-4 leading-relaxed">
        Leave empty only if the network has no password.
      </p>
    </>
  );
}
